import React, { useState } from 'react';
import { SubscriptionDetails } from '../../types/subscription';
import { subscriptionApi } from '../../services/subscription.service';

interface SubscriptionDetailsProps {
  details: SubscriptionDetails;
  onUpdate: () => void;
}

export const SubscriptionDetailsComponent: React.FC<SubscriptionDetailsProps> = ({
  details,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleToggleAutoRenew = async () => {
    try {
      setLoading(true);
      await subscriptionApi.updateSubscription(details.currentSubscription.id, {
        autoRenew: !details.currentSubscription.autoRenew,
      });
      onUpdate();
      setError(null);
    } catch (err) {
      setError('Failed to update auto-renew setting');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      await subscriptionApi.cancelSubscription({
        reason: 'User requested cancellation',
        immediate: false,
      });
      onUpdate();
      setShowCancelConfirm(false);
      setError(null);
    } catch (err) {
      setError('Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const sub = details.currentSubscription;
  const usage = details.usageStats;
  const plan = sub.plan;
  const currencySymbol = '₹';

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Current Plan Card */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg border border-blue-200">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{plan?.name}</h2>
            <p className="text-gray-600 text-lg">{plan?.description}</p>
          </div>
          <span className={`px-4 py-2 rounded-full font-semibold text-sm ${getStatusColor(sub.status)}`}>
            {sub.status}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-gray-600 text-sm">Monthly Price</p>
            <p className="text-2xl font-bold text-gray-800">
              {currencySymbol}{sub.price}
              <span className="text-lg text-gray-600">/mo</span>
            </p>
          </div>
          <div>
            <p className="text-gray-600 text-sm">Start Date</p>
            <p className="text-lg font-semibold text-gray-800">{formatDate(sub.startDate)}</p>
          </div>
          <div>
            <p className="text-gray-600 text-sm">Renewal Date</p>
            <p className="text-lg font-semibold text-gray-800">
              {formatDate(sub.renewalDate || details.nextRenewalDate)}
            </p>
          </div>
          <div>
            <p className="text-gray-600 text-sm">Days Until Renewal</p>
            <p className="text-lg font-semibold text-gray-800">
              {details.daysUntilExpiry || 'N/A'}
            </p>
          </div>
        </div>

        {/* Auto Renew Toggle */}
        <div className="flex items-center justify-between bg-white p-4 rounded">
          <div>
            <p className="font-medium text-gray-800">Auto Renewal</p>
            <p className="text-sm text-gray-600">
              {sub.autoRenew
                ? 'Your subscription will automatically renew on the renewal date'
                : 'Your subscription will not renew automatically'}
            </p>
          </div>
          <button
            onClick={handleToggleAutoRenew}
            disabled={loading}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition ${
              sub.autoRenew ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition ${
                sub.autoRenew ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Usage Statistics */}
      <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Usage Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <p className="text-gray-600 text-sm mb-2">Practice Attempts</p>
            <p className="text-3xl font-bold text-gray-800">
              {usage.studentsUsed}
              <span className="text-lg text-gray-600">
                {' '}
                / {plan?.features.maxExamAttempts === -1 ? '∞' : plan?.features.maxExamAttempts ?? plan?.features.maxAssignments}
              </span>
            </p>
            {plan?.features.maxExamAttempts !== -1 && (
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(usage.studentsUsed / (plan?.features.maxExamAttempts || plan?.features.maxAssignments || 1)) * 100}%`,
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <p className="text-gray-600 text-sm mb-2">Assignments</p>
            <p className="text-3xl font-bold text-gray-800">
              {usage.classesUsed}
              <span className="text-lg text-gray-600">
                {' '}
                / {plan?.features.maxAssignments === -1 ? '∞' : plan?.features.maxAssignments}
              </span>
            </p>
            {plan?.features.maxAssignments !== -1 && (
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(usage.classesUsed / (plan?.features.maxAssignments || 1)) * 100}%`,
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <p className="text-gray-600 text-sm mb-2">Exams Created</p>
            <p className="text-3xl font-bold text-gray-800">{usage.examsCreated}</p>
            <p className="text-sm text-gray-600 mt-2">
              {usage.examAttempts} exam attempts
            </p>
          </div>
        </div>
      </div>

      {/* Plan Features */}
      <div>
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Plan Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-4">Included Features</h4>
            <ul className="space-y-3">
              {plan?.features.hasAnalytics && (
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700">Learning Analytics</span>
                </li>
              )}
              {plan?.features.hasAdvancedReporting && (
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700">Advanced Progress Reports</span>
                </li>
              )}
              {plan?.features.examResultsAccess && (
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700">Detailed Exam Results</span>
                </li>
              )}
              {plan?.features.certificateGeneration && (
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700">Certificate Generation</span>
                </li>
              )}
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span className="text-gray-700">
                  Support: {plan?.features.supportLevel}
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-4">Billing Information</h4>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-600">Subscription ID</p>
                <p className="font-mono text-gray-800 break-all">{sub.id}</p>
              </div>
              <div>
                <p className="text-gray-600">Stripe Subscription ID</p>
                <p className="font-mono text-gray-800">{sub.stripeSubscriptionId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-600">Currency</p>
                <p className="text-gray-800">{sub.currency}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
        <h3 className="text-lg font-bold text-red-800 mb-4">Danger Zone</h3>
        <p className="text-red-700 text-sm mb-4">
          Cancelling your subscription will end access at the end of your current billing cycle.
        </p>
        <button
          onClick={() => setShowCancelConfirm(true)}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-6 py-2 rounded font-medium transition"
        >
          Cancel Subscription
        </button>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Cancel Subscription?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? You'll have access until the end of
              your billing cycle.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-medium"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded font-medium"
              >
                {loading ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetailsComponent;

